import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminCartPanel } from "./components/AdminCartPanel";
import { AdminCatalogActions } from "./components/AdminCatalogActions";
import { AdminFulfillmentPanel } from "./components/AdminFulfillmentPanel";
import { AppNav } from "./components/AppNav";
import { RouteLoading } from "./components/RouteLoading";
import { Seo } from "./components/Seo";
import { SiteHeader } from "./components/SiteHeader";
import { useAdminCatalog } from "./hooks/useAdminCatalog";
import { useAppRoute } from "./hooks/useAppRoute";
import { useCatalogData } from "./hooks/useCatalogData";
import { useCart } from "./hooks/useCart";
import { useCheckout } from "./hooks/useCheckout";
import { useOrders } from "./hooks/useOrders";
import { useSession } from "./hooks/useSession";
import { useTheme } from "./hooks/useTheme";
import { stockLabel } from "./lib/catalog";
import { CatalogPage } from "./pages/CatalogPage";
import { CustomerOrderPage } from "./pages/CustomerOrderPage";
import { PaymentPage } from "./pages/PaymentPage";
import { ProductPage } from "./pages/ProductPage";
import type { AddCatalogForm } from "./components/AddProductModal";
import {
  buildCatalogFilterOptions,
  type CatalogFilters,
  formatFitmentLabel,
  matchesCatalogFilters,
  matchesProductQuery,
} from "./lib/catalog";
import type { OrderSummary, Product } from "./types/catalog";

const DEFAULT_CATALOG_FILTERS: CatalogFilters = {
  brand: "",
  condition: "",
  engine: "",
  make: "",
  model: "",
  year: "",
};

type ToastTone = "error" | "success" | "warning";

type ToastMessage = {
  id: number;
  message: string;
  tone: ToastTone;
};

function isFulfillmentDone(order: OrderSummary) {
  return (
    order.status === "completed" ||
    order.status === "cancelled" ||
    (order.paymentStatus === "paid" && order.allPacked === true)
  );
}

function orderReference(order: OrderSummary) {
  return order.orderCode || order.id.slice(0, 8);
}

const AddProductModal = lazy(async () => {
  const module = await import("./components/AddProductModal");
  return { default: module.AddProductModal };
});

const CartPanel = lazy(async () => {
  const module = await import("./components/CartPanel");
  return { default: module.CartPanel };
});

const OrdersPanel = lazy(async () => {
  const module = await import("./components/OrdersPanel");
  return { default: module.OrdersPanel };
});

export function App() {
  const { theme, toggleTheme } = useTheme();
  const session = useSession();
  const isAdmin = session.role === "admin";
  const isAdminHost = window.location.hostname.startsWith("admin.");
  const {
    catalogStatus,
    productCategories,
    products,
    setProductCategories,
    setProducts,
  } = useCatalogData();
  const {
    activeRoute,
    isRouteLoading,
    navigate,
    navigateToCategory,
    navigateToOrder,
    navigateToPayment,
    navigateToProduct,
    routeCategory,
    routeOrderCode,
    routeProductId,
  } = useAppRoute();
  const {
    cartCount,
    cartItems,
    cartStatus,
    cartTotalKes,
    clearCart,
    customerProfile,
    handleAddToCart,
    handleRemoveFromCart,
    handleUpdateCartQuantity,
    rememberCustomerProfile,
  } = useCart();
  const {
    handleOrderClaim,
    handleOrderRelease,
    handleOrderStatusChange,
    orderListStatus,
    orders,
    prependOrder,
    refreshOrders,
  } = useOrders({ currentAdminEmail: session.email, isAdmin });
  const [query, setQuery] = useState("");
  const [fitment, setFitment] = useState("");
  const [catalogFilters, setCatalogFilters] = useState<CatalogFilters>(DEFAULT_CATALOG_FILTERS);
  const [selectedSearchProduct, setSelectedSearchProduct] = useState<Product | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [activeFulfillmentOrderId, setActiveFulfillmentOrderId] = useState("");
  const [addCatalogInitialForm, setAddCatalogInitialForm] = useState<AddCatalogForm>("product");
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const toastIdRef = useRef(0);
  const adminFulfillmentOrders = useMemo(() => {
    if (!isAdmin || !session.email) {
      return [];
    }

    return orders.filter((order) => order.claimedBy === session.email);
  }, [isAdmin, orders, session.email]);
  const {
    checkoutStatus,
    checkoutStatusTone,
    handleCheckout,
    isCheckoutSubmitting,
  } = useCheckout({
    cartItems,
    cartTotalKes,
    onCartCleared: clearCart,
    onCheckoutComplete: (order) => {
      if (order.orderCode) {
        navigateToPayment(order.orderCode);
        return;
      }

      navigate("orders");
    },
    onCustomerProfileRemembered: rememberCustomerProfile,
    onOrderCreated: prependOrder,
  });

  const showToast = useCallback((message: string, tone: ToastTone = "warning") => {
    const id = toastIdRef.current + 1;
    toastIdRef.current = id;
    setToasts((currentToasts) => [...currentToasts.slice(-2), { id, message, tone }]);

    window.setTimeout(() => {
      setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
    }, 5200);
  }, []);
  const {
    handleDeleteCategory,
    handleDeleteProduct,
    handleSaveCategory,
    handleSaveProduct,
    handleTogglePromoted,
    productSaveStatus,
  } = useAdminCatalog({
    onProductSaved: () => undefined,
    setProductCategories,
    setProducts,
  });
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const catalogFilterOptions = useMemo(() => buildCatalogFilterOptions(products), [products]);

  const matchingProducts = useMemo(
    () => products.filter((product) => matchesProductQuery(product, query)),
    [products, query]
  );

  const searchSuggestions = query.trim() ? matchingProducts.slice(0, 6) : [];

  const fitmentOptions = useMemo(
    () => Array.from(new Set(products.map((product) => formatFitmentLabel(product)).filter(Boolean))).sort(),
    [products]
  );

  const hasSearchSelection = selectedSearchProduct !== null;
  const isCatalogSearchResults = hasSearchSelection && (activeRoute === "catalog" || activeRoute === "category");
  const selectedFitment = fitment.trim().toLowerCase();

  const feedProducts = useMemo(() => {
    const normalizedQuery = isCatalogSearchResults ? query.trim().toLowerCase() : "";
    const normalizedFitment = isCatalogSearchResults ? selectedFitment : "";
    const activeCategory = isCatalogSearchResults ? "" : activeRoute === "category" ? routeCategory : "";
    const activeFilters = isCatalogSearchResults ? catalogFilters : DEFAULT_CATALOG_FILTERS;

    return products.filter((product) => {
      const fitmentLabel = formatFitmentLabel(product).toLowerCase();

      return (
        (!activeCategory || product.category === activeCategory) &&
        matchesProductQuery(product, normalizedQuery) &&
        (!normalizedFitment || fitmentLabel === normalizedFitment) &&
        matchesCatalogFilters(product, activeFilters)
      );
    });
  }, [activeRoute, catalogFilters, isCatalogSearchResults, products, query, routeCategory, selectedFitment]);

  const curatedProducts = useMemo(() => {
    const promotedProducts = feedProducts.filter((product) => product.isPromoted);
    const fallbackProducts = promotedProducts.length > 0 ? promotedProducts : feedProducts;

    return [...fallbackProducts].sort((firstProduct, secondProduct) => {
      if (firstProduct.isPromoted !== secondProduct.isPromoted) {
        return firstProduct.isPromoted ? -1 : 1;
      }

      return firstProduct.name.localeCompare(secondProduct.name);
    });
  }, [feedProducts]);

  const visibleCategories = useMemo(() => {
    if (productCategories.length > 0) {
      return productCategories;
    }

    return [];
  }, [productCategories]);

  const selectedProduct = useMemo(
    () => products.find((product) => product.id === routeProductId) ?? null,
    [products, routeProductId]
  );

  const canSearchCatalogue = activeRoute === "catalog" || activeRoute === "category";
  const canManageCatalogRoute = activeRoute === "catalog" || activeRoute === "category";
  const routeTitle = (() => {
    if (isCatalogSearchResults) {
      return "Search results";
    }

    if (activeRoute === "category") {
      return routeCategory;
    }

    if (activeRoute === "product") {
      return selectedProduct?.name ?? "Product";
    }

    if (activeRoute === "payment") {
      return "Payment";
    }

    if (activeRoute === "order") {
      return "Order";
    }

    return activeRoute === "catalog" ? "Catalogue" : activeRoute[0].toUpperCase() + activeRoute.slice(1);
  })();
  const routeShellKey = (() => {
    if (isCatalogSearchResults) {
      return `search:${selectedSearchProduct?.id ?? "active"}`;
    }

    if (activeRoute === "category") {
      return `category:${routeCategory}`;
    }

    if (activeRoute === "product") {
      return `product:${routeProductId}`;
    }

    if (activeRoute === "payment" || activeRoute === "order") {
      return `${activeRoute}:${routeOrderCode}`;
    }

    return activeRoute;
  })();
  const selectedProductStockLabel = selectedProduct ? stockLabel(selectedProduct, isAdmin) : "";

  useEffect(() => {
    if (fitment && !fitmentOptions.includes(fitment)) {
      setFitment("");
    }
  }, [fitment, fitmentOptions]);

  useEffect(() => {
    setCatalogFilters((currentFilters) => {
      const nextFilters: CatalogFilters = { ...currentFilters };
      let shouldUpdate = false;

      if (nextFilters.brand && !catalogFilterOptions.brands.includes(nextFilters.brand)) {
        nextFilters.brand = "";
        shouldUpdate = true;
      }

      if (nextFilters.condition && !catalogFilterOptions.conditions.includes(nextFilters.condition)) {
        nextFilters.condition = "";
        shouldUpdate = true;
      }

      if (nextFilters.engine && !catalogFilterOptions.engines.includes(nextFilters.engine)) {
        nextFilters.engine = "";
        shouldUpdate = true;
      }

      if (nextFilters.make && !catalogFilterOptions.makes.includes(nextFilters.make)) {
        nextFilters.make = "";
        shouldUpdate = true;
      }

      if (nextFilters.model && !catalogFilterOptions.models.includes(nextFilters.model)) {
        nextFilters.model = "";
        shouldUpdate = true;
      }

      if (nextFilters.year && !catalogFilterOptions.years.includes(nextFilters.year)) {
        nextFilters.year = "";
        shouldUpdate = true;
      }

      return shouldUpdate ? nextFilters : currentFilters;
    });
  }, [catalogFilterOptions]);

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (!canSearchCatalogue && isSearchOpen) {
      setIsSearchOpen(false);
    }
  }, [canSearchCatalogue, isSearchOpen]);

  useEffect(() => {
    if (canSearchCatalogue) {
      return;
    }

    setSelectedSearchProduct(null);
    setCatalogFilters(DEFAULT_CATALOG_FILTERS);
    setFitment("");
  }, [canSearchCatalogue]);

  useEffect(() => {
    if (!isAdmin) {
      setActiveFulfillmentOrderId("");
      return;
    }

    if (!activeFulfillmentOrderId) {
      return;
    }

    const hasVisibleOrder = adminFulfillmentOrders.some((order) => order.id === activeFulfillmentOrderId);

    if (!hasVisibleOrder) {
      setActiveFulfillmentOrderId("");
    }
  }, [activeFulfillmentOrderId, adminFulfillmentOrders, isAdmin]);

  function startFulfillment(order: OrderSummary) {
    const activeClaim = orders.find(
      (candidate) =>
        candidate.claimedBy === session.email &&
        candidate.id !== order.id &&
        !isFulfillmentDone(candidate)
    );

    if (activeClaim) {
      showToast(
        `Finish active fulfillment ${orderReference(activeClaim)} before starting another order.`,
        "warning"
      );
      return;
    }

    if (order.claimedBy && order.claimedBy !== session.email) {
      showToast(`This order is already claimed by ${order.claimedBy}.`, "warning");
      return;
    }

    if (order.claimedBy === session.email) {
      setActiveFulfillmentOrderId(order.id);
      navigate("cart");
      return;
    }

    void (async () => {
      const claimResult = await handleOrderClaim(order.id);

      if (claimResult.ok && claimResult.orderId) {
        setActiveFulfillmentOrderId(claimResult.orderId);
        navigate("cart");
        return;
      }

      showToast(claimResult.message, "error");
    })();
  }

  function toggleSearch() {
    setIsSearchOpen((currentValue) => !currentValue);
  }

  function chooseSearchSuggestion(product: Product) {
    setQuery(product.name);
    setFitment(formatFitmentLabel(product));
    setCatalogFilters({
      brand: product.brand,
      condition: product.condition,
      engine: product.fitmentEngine,
      make: product.fitmentMake,
      model: product.fitmentModel,
      year: String(product.fitmentYearFrom ?? product.fitmentYearTo ?? ""),
    });
    setSelectedSearchProduct(product);
    setIsSearchOpen(false);
  }

  function handleQueryChange(nextQuery: string) {
    setQuery(nextQuery);

    if (selectedSearchProduct) {
      setSelectedSearchProduct(null);
      setCatalogFilters(DEFAULT_CATALOG_FILTERS);
      setFitment("");
    }
  }

  function openAddCatalogSheet(form: AddCatalogForm) {
    setAddCatalogInitialForm(form);
    setIsAddProductOpen(true);
  }

  return (
    <div className="app-shell">
      <Seo
        activeRoute={activeRoute}
        curatedProducts={curatedProducts}
        product={selectedProduct}
        routeCategory={routeCategory}
      />
      <SiteHeader
        canSearchCatalogue={canSearchCatalogue}
        isSearchOpen={isSearchOpen}
        query={query}
        routeTitle={routeTitle}
        searchInputRef={searchInputRef}
        searchSuggestions={searchSuggestions}
        theme={theme}
        onChooseSearchSuggestion={chooseSearchSuggestion}
        onQueryChange={handleQueryChange}
        onToggleSearch={toggleSearch}
        onToggleTheme={toggleTheme}
      />
      <AppNav
        activeRoute={activeRoute}
        cartCount={cartCount}
        isAdmin={isAdmin}
        onNavigate={navigate}
      />

      <main className="app-main">
        <div className="route-shell" key={routeShellKey}>
          {isRouteLoading ? (
            <RouteLoading label={`Loading ${routeTitle}`} route={activeRoute} />
          ) : (
            <>
              {activeRoute === "catalog" || activeRoute === "category" ? (
                <CatalogPage
                  activeRoute={activeRoute}
                  catalogStatus={catalogStatus}
                  catalogFilterOptions={catalogFilterOptions}
                  catalogFilters={catalogFilters}
                  curatedProducts={curatedProducts}
                  isAdmin={isAdmin}
                  fitment={fitment}
                  fitmentOptions={fitmentOptions}
                  productSaveStatus={productSaveStatus}
                  routeCategory={routeCategory}
                  searchSelectionLabel={selectedSearchProduct?.name ?? ""}
                  showSearchResults={isCatalogSearchResults}
                  visibleCategories={visibleCategories}
                  onAddToCart={handleAddToCart}
                  onNavigateToCategory={navigateToCategory}
                  onNavigateToProduct={(product) => navigateToProduct(product.id)}
                  onCatalogFilterChange={setCatalogFilters}
                  onFitmentChange={setFitment}
                  onTogglePromoted={(nextProduct) => {
                    void handleTogglePromoted(nextProduct);
                  }}
                />
              ) : null}

              {activeRoute === "product" ? (
                <ProductPage
                  isAdmin={isAdmin}
                  product={selectedProduct}
                  stockLabel={selectedProductStockLabel}
                  onAddToCart={handleAddToCart}
                />
              ) : null}

              {activeRoute === "cart" ? (
                <>
                  {!isAdmin && cartStatus ? <p className="floating-status">{cartStatus}</p> : null}
                  {isAdmin ? (
                    <>
                      <Suspense fallback={<RouteLoading label="Loading fulfillment orders" route="cart" />}>
                        <AdminCartPanel
                          currentAdminEmail={session.email}
                          orders={adminFulfillmentOrders}
                          onOpenFulfillment={(orderId) => {
                            setActiveFulfillmentOrderId(orderId);
                          }}
                          onRefresh={() => {
                            void refreshOrders();
                          }}
                        />
                      </Suspense>
                    </>
                  ) : (
                    <Suspense fallback={<RouteLoading label="Loading cart" route="cart" />}>
                      <CartPanel
                        checkoutStatus={checkoutStatus}
                        checkoutStatusTone={checkoutStatusTone}
                        customerProfile={customerProfile}
                        isCheckoutSubmitting={isCheckoutSubmitting}
                        items={cartItems}
                        totalKes={cartTotalKes}
                        onCheckout={(checkout) => {
                          void handleCheckout(checkout);
                        }}
                        onRemove={handleRemoveFromCart}
                        onUpdateQuantity={handleUpdateCartQuantity}
                      />
                    </Suspense>
                  )}
                </>
              ) : null}

              {activeRoute === "payment" ? (
                <PaymentPage
                  orderCode={routeOrderCode}
                  onOpenOrder={navigateToOrder}
                />
              ) : null}

              {activeRoute === "order" ? (
                <CustomerOrderPage
                  orderCode={routeOrderCode}
                  onOpenPayment={navigateToPayment}
                />
              ) : null}

              {activeRoute === "orders" ? (
                <>
                  {orderListStatus ? <p className="floating-status">{orderListStatus}</p> : null}
                  <Suspense fallback={<RouteLoading label="Loading orders" route="orders" />}>
                    <OrdersPanel
                      isAdmin={isAdmin}
                      currentAdminEmail={session.email}
                      orders={orders}
                      onStartFulfillment={startFulfillment}
                      onOpenOrder={navigateToOrder}
                      onRefresh={() => {
                        void refreshOrders();
                      }}
                    />
                  </Suspense>
                </>
              ) : null}
            </>
          )}
        </div>

        {activeFulfillmentOrderId ? (
          <Suspense fallback={null}>
            <AdminFulfillmentPanel
              orderId={activeFulfillmentOrderId}
              onClose={() => setActiveFulfillmentOrderId("")}
              onReleaseClaim={async (orderId) => {
                return handleOrderRelease(orderId);
              }}
              onOrderStatusChange={async (orderId, status) => {
                return handleOrderStatusChange(orderId, status);
              }}
              onRefreshOrders={async () => {
                await refreshOrders();
              }}
            />
          </Suspense>
        ) : null}

        {isAddProductOpen ? (
          <Suspense fallback={null}>
            <AddProductModal
              categories={productCategories}
              products={products}
              initialForm={addCatalogInitialForm}
              isOpen={isAddProductOpen}
              onClose={() => setIsAddProductOpen(false)}
              onCategorySubmit={handleSaveCategory}
              onCategoryDelete={handleDeleteCategory}
              onProductSubmit={handleSaveProduct}
              onProductDelete={handleDeleteProduct}
            />
          </Suspense>
        ) : null}
      </main>
      <div className="toast-viewport" role="region" aria-label="Notifications">
        {toasts.map((toast) => (
          <div
            className={`toast toast--${toast.tone}`}
            key={toast.id}
            role={toast.tone === "error" ? "alert" : "status"}
          >
            <p>{toast.message}</p>
            <button
              className="toast__dismiss"
              type="button"
              onClick={() => {
                setToasts((currentToasts) =>
                  currentToasts.filter((currentToast) => currentToast.id !== toast.id)
                );
              }}
            >
              Dismiss
            </button>
          </div>
        ))}
      </div>
      {canManageCatalogRoute && !isAddProductOpen ? (
        <AdminCatalogActions
          isAdmin={isAdmin}
          isAdminHost={isAdminHost}
          session={session}
          onAddCatalogOpen={openAddCatalogSheet}
        />
      ) : null}
    </div>
  );
}
